"use client";

import { GenreBadgeListFromData } from "@/components/shared/genre-badge-list";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";

// Using the artist type from getArtistProfileWithSets return type
interface ArtistProfileData {
	artist_id: string;
	artist_name: string;
	artist_slug: string;
	artist_image_url: string | null;
	artist_bio: string | null;
	spotify_genres: string[] | null;
	manual_genres: string[] | null;
	website_url: string | null;
	instagram_url: string | null;
	twitter_url: string | null;
	facebook_url: string | null;
	tiktok_url: string | null;
	soundcloud_url: string | null;
	apple_music_url: string | null;
	spotify_url: string | null;
	youtube_url: string | null;
	store_url: string | null;
	total_sets: number;
	festivals_played: number;
	total_shows: number;
	total_reviewers: number;
	average_rating: number | null;
}

import { Badge } from "@workspace/ui/components/badge";
import { Card, CardContent, CardHeader } from "@workspace/ui/components/card";
import { cn } from "@workspace/ui/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { memo } from "react";
import { SocialButtonGroup } from "@/components/shared/social-button-group";

interface ArtistProfileCardProps {
	artist: ArtistProfileData;
	aliasUsed?: string;
	priority?: boolean;
	aliases?: Array<{
		alias_name: string;
		is_primary: boolean;
	}>;
}

export const ArtistProfileCard = memo<ArtistProfileCardProps>(
	function ArtistHeaderCard({ artist, aliasUsed, priority = false, aliases }) {
		// Determine the display name for the card
		const cardDisplayName = aliasUsed || artist.artist_name;

		return (
			<Card className="w-full border bg-card p-3">
				<CardHeader className="p-0">
					<div className="flex gap-4">
						{/* Square artist photo with rounded corners */}
						<div className="h-20 w-20 flex-shrink-0">
							<Link
								href={`/artists/${createArtistSlug(artist.artist_name, artist.artist_slug)}`}
								className="group block h-full w-full rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2"
								aria-label={`View ${artist.artist_name} profile`}
							>
								<div
									className={cn(
										"h-full w-full overflow-hidden rounded-lg bg-muted transition-all duration-200",
										"group-hover:ring-4 group-hover:ring-primary/10",
										"group-active:scale-95 group-active:ring-primary/20",
									)}
								>
									<div className="h-full w-full transition-transform duration-200 group-hover:scale-105 group-active:scale-100">
										{artist.artist_image_url ? (
											<Image
												src={artist.artist_image_url}
												alt={artist.artist_name}
												width={80}
												height={80}
												className="h-full w-full object-cover"
												priority={priority}
												unoptimized
											/>
										) : (
											<div className="flex h-full w-full items-center justify-center bg-muted">
												<span className="font-semibold text-2xl text-muted-foreground">
													{artist.artist_name.charAt(0).toUpperCase()}
												</span>
											</div>
										)}
									</div>
								</div>
							</Link>
						</div>

						<div className="min-w-0 flex-1">
							<h1 className="mb-3 font-bold text-2xl leading-tight">
								{cardDisplayName}
								{aliasUsed && (
									<span className="text-muted-foreground/50">
										{" "}
										aka {artist.artist_name}
									</span>
								)}
							</h1>

							<div className="mb-3">
								<GenreBadgeListFromData
									spotifyGenres={artist.spotify_genres || null}
									manualGenres={artist.manual_genres || null}
									className="gap-2"
								/>
							</div>

							{/* Also known as section */}
							{aliases && aliases.length > 0 && (
								<div className="mb-3">
									<span className="text-muted-foreground text-xs mr-2">
										Also known as:
									</span>
									{aliases.map((alias, index) => (
										<span key={alias.alias_name} className="text-foreground text-xs">
											{alias.alias_name}
											{alias.is_primary && <span className="text-primary"> ★</span>}
											{index < aliases.length - 1 && ", "}
										</span>
									))}
								</div>
							)}

							{/* Stats badges like user profile */}
							<div className="flex flex-wrap gap-2">
								<Badge variant="outline" className="text-xs">
									{artist.festivals_played} Festival
									{artist.festivals_played !== 1 ? "s" : ""}
								</Badge>
								<Badge variant="outline" className="text-xs">
									{artist.total_sets} Set{artist.total_sets !== 1 ? "s" : ""}
								</Badge>
								<Badge variant="outline" className="text-xs">
									{artist.total_shows} Show{artist.total_shows !== 1 ? "s" : ""}
								</Badge>
								{artist.average_rating && (
									<Badge variant="outline" className="text-xs">
										★ {artist.average_rating.toFixed(1)} avg
									</Badge>
								)}
							</div>

							{/* Social media buttons */}
							<SocialButtonGroup
								name={artist.artist_name}
								links={{
									spotify: artist.spotify_url,
									youtube: artist.youtube_url,
									instagram: artist.instagram_url,
									twitter: artist.twitter_url,
									facebook: artist.facebook_url,
									tiktok: artist.tiktok_url,
									soundcloud: artist.soundcloud_url,
									appleMusic: artist.apple_music_url,
									website: artist.website_url,
									store: artist.store_url,
								}}
								size="xs"
								className="mt-3"
							/>
						</div>
					</div>
				</CardHeader>

				{artist.artist_bio && (
					<CardContent className="p-0 pt-3">
						<p className="text-muted-foreground text-sm leading-relaxed">
							{artist.artist_bio}
						</p>
					</CardContent>
				)}
			</Card>
		);
	},
);
